/**
 * 「添加到主屏幕」提示卡 — 仅在浏览器模式（非 standalone）显示。
 *
 * Android / 支持 beforeinstallprompt：一键安装按钮。
 * iOS Safari：无该事件，展开手动「分享 → 添加到主屏幕」引导。
 * 用户关掉后记进 localStorage，不再打扰。
 */

import { useState } from 'react'

import { usePwaInstall } from '../lib/pwa'
import { useLang } from '../contexts/LanguageContext'
import { IconClose, IconShare, IconPlus } from './icons'

const DISMISS_KEY = 'cortex-m-install-hidden'

export default function InstallPrompt() {
  const { t } = useLang()
  const { isStandalone, isIos, canPrompt, promptInstall } = usePwaInstall()
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISS_KEY) === '1'
    } catch {
      return false
    }
  })
  const [expanded, setExpanded] = useState(false)

  // standalone 已是 PWA / 已关掉 / 既不能一键装也不是 iOS → 不显示
  if (isStandalone || dismissed || (!canPrompt && !isIos)) return null

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {}
    setDismissed(true)
  }

  return (
    <div className="reveal overflow-hidden rounded-lg border border-line-subtle bg-panel">
      <div className="flex items-center gap-3 px-3.5 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-accent/15 text-accent-light">
          <IconPlus className="h-4 w-4" strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-primary">{t('install.title')}</div>
          <div className="text-xs text-muted">{t('install.subtitle')}</div>
        </div>
        {canPrompt ? (
          <button
            onClick={() => void promptInstall()}
            className="shrink-0 rounded-md bg-accent px-3 py-1.5 text-xs font-medium text-white"
          >
            {t('install.add')}
          </button>
        ) : (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="shrink-0 rounded-md border border-line px-3 py-1.5 text-xs font-medium text-secondary"
          >
            {expanded ? t('install.collapse') : t('install.how')}
          </button>
        )}
        <button onClick={dismiss} aria-label={t('common.close')} className="shrink-0 text-faint">
          <IconClose className="h-4 w-4" />
        </button>
      </div>

      {expanded && isIos && (
        <div className="flex items-start gap-2 border-t border-line-subtle px-3.5 py-3 text-xs leading-relaxed text-muted">
          <IconShare className="mt-0.5 h-4 w-4 shrink-0 text-info" />
          <span>{t('install.iosHint')}</span>
        </div>
      )}
    </div>
  )
}
